import { FileWarning } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { Select } from '@/components/ui/Select';
import { InlineError } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';
import { PersonPicker } from '@/features/people/components/PersonPicker';
import type { PersonSelection } from '@/features/people/types';
import { useAddFromLms } from '@/features/people/use-people';
import { addFromLmsErrorMessage } from '@/features/roles-access/invite-feedback';
import type { Role } from '@/features/roles-access/types';

import { useAssignRole } from '../use-users';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  open: boolean;
  roles: Role[];
  /** Los roles no cargaron: el selector quedaría vacío sin explicación (UX-ERR-01). */
  rolesError?: { onRetry: () => void; retrying: boolean; cause: unknown } | null;
  onClose: () => void;
}

type Person = Exclude<PersonSelection, { kind: 'invite' }>;

/** Agregar usuario: busca a una persona del QMS o del LMS (aunque nunca haya entrado) y
 *  le asigna un rol. A la del LMS se la agrega al QMS en ese momento. Los proveedores
 *  externos sin usuario se invitan desde los accesos temporales. */
export function AddUserModal({
  open,
  roles,
  rolesError = null,
  onClose,
}: Props): ReactElement | null {
  const assign = useAssignRole();
  const addFromLms = useAddFromLms();
  const { showToast } = useToast();
  const [person, setPerson] = useState<Person | null>(null);
  const [roleId, setRoleId] = useState('');
  useEffect(() => {
    if (open) {
      setPerson(null);
      setRoleId('');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const busy = assign.isPending || addFromLms.isPending;
  const role = roles.find((r) => r.id === roleId);
  const valid = person !== null && role !== undefined;

  async function submit(): Promise<void> {
    if (!valid || busy) {
      return;
    }
    let userId: string;
    if (person.kind === 'lms') {
      try {
        userId = await addFromLms.mutateAsync({ authSubject: person.authSubject });
      } catch (e) {
        showToast(addFromLmsErrorMessage(e as Error), 'error');
        return;
      }
    } else {
      userId = person.userId;
    }
    try {
      await assign.mutateAsync({ userId, roleId: role.id });
    } catch {
      showToast('No se pudo asignar el rol. Intenta de nuevo.', 'error');
      return;
    }
    showToast(
      person.kind === 'lms'
        ? `Se agregó a ${person.name} al QMS con el rol ${role.name}. Podrá entrar con su cuenta del LMS.`
        : `Rol ${role.name} asignado a ${person.name}`,
    );
    onClose();
  }

  return (
    <ModalShell
      open
      title="Agregar usuario"
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => void submit()}
            disabled={!valid || busy}
          >
            {busy ? 'Agregando…' : 'Agregar'}
          </button>
        </>
      }
    >
      <p className="soft" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
        Busca a la persona por nombre o correo. Puedes asignarle un rol aunque todavía no haya
        entrado al QMS.
      </p>
      <div className="form-grid">
        {person === null ? (
          <PersonPicker
            allowInvite={false}
            onSelect={(s) => {
              if (s.kind !== 'invite') {
                setPerson(s);
              }
            }}
          />
        ) : (
          <div className="fld">
            <span className="fld-lab">Persona</span>
            <div className="person-chosen">
              <strong>{person.name}</strong>
              <span className="soft">
                {person.kind === 'lms' ? 'Del LMS: se agregará al QMS' : 'En el QMS'}
              </span>
              <button type="button" className="btn mut sm" onClick={() => setPerson(null)}>
                Cambiar
              </button>
            </div>
          </div>
        )}
        <label className="fld">
          <span className="fld-lab">Rol</span>
          <Select
            value={roleId}
            placeholder="Selecciona un rol…"
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onChange={setRoleId}
          />
        </label>
        {rolesError && (
          <InlineError
            icon={<FileWarning />}
            onRetry={rolesError.onRetry}
            retrying={rolesError.retrying}
            dense
          >
            No se pudieron cargar los roles. {errorAdvice(rolesError.cause)}
          </InlineError>
        )}
      </div>
    </ModalShell>
  );
}
